import { mapValues, update } from 'lodash';
import { ApiFaultOrWarnings } from '../api/api.fault.interfaces';
import { clone } from '../utility/common';
import { ErrorConverter } from './ErrorConverter';
import {
  CAPTURED_ERROR_API_MESSAGE_CONFIG_PATHS,
  ERROR_LOG_RESTRICTED_MODULES,
  REDACTED_FAULT_MESSAGES_MAP,
  REDACTED_HEADERS,
  REDACTED_MESSAGE
} from './errorHandling.config';
import {
  CapturedApiError,
  CapturedError,
  CapturedErrorApiMessage,
  CapturedErrorApiMessageConfigPath
} from './errorHandling.types';

/**
 * Pure sanitization functions: redact sensitive data from captured errors
 * before submission to the logging service.
 *
 * Consolidates logic from errorHandling.sanitizing.helpers and the
 * transform pipeline in errorHandling.capturedError.helpers.
 */
export class ErrorSanitizer {
  /**
   * Run the full sanitization pipeline on a captured error:
   * 1. URL-encode baseURL (prevents firewall from dropping the request)
   * 2. Delete duplicate response.config
   * 3. Redact sensitive HTTP headers (Authorization, CD-SessionId)
   * 4. Redact fault messages for sensitive fault codes (e.g. session expired)
   * 5. Redact request/response bodies for restricted modules
   */
  static sanitize(error: CapturedError): CapturedError {
    let sanitized = clone(error);

    sanitized = ErrorSanitizer.encodeBaseUrl(sanitized);
    sanitized = ErrorSanitizer.removeResponseConfigDuplicate(sanitized);

    if (ErrorConverter.isCapturedErrorApiRelatedError(sanitized)) {
      sanitized = ErrorSanitizer.redactHeaders(sanitized);

      if (ErrorConverter.isCapturedApiErrorCoreFaultError(sanitized)) {
        sanitized.applicationContext.apiMessage.response!.data =
          ErrorSanitizer.redactFaultMessage(
            sanitized.applicationContext.apiMessage.response!
              .data as ApiFaultOrWarnings
          );
      }

      sanitized = ErrorSanitizer.redactRestrictedModulePayload(sanitized);
    }

    return sanitized;
  }

  /**
   * Redact sensitive HTTP headers across all config paths
   * (config.headers, request.config.headers, response.config.headers).
   */
  static redactHeaders(error: CapturedApiError): CapturedApiError {
    const hasHeaders: Record<CapturedErrorApiMessageConfigPath, boolean> = {
      config: !!error.applicationContext.apiMessage.config?.headers,
      'request.config':
        !!error.applicationContext.apiMessage.request?.config?.headers,
      'response.config':
        !!error.applicationContext.apiMessage.response?.config?.headers
    };

    const result = clone(error);

    REDACTED_HEADERS.forEach((header) => {
      CAPTURED_ERROR_API_MESSAGE_CONFIG_PATHS.forEach((property) => {
        if (hasHeaders[property]) {
          result.applicationContext.apiMessage =
            ErrorSanitizer.redactSingleHeader(
              result.applicationContext.apiMessage,
              property,
              header
            );
        }
      });
    });

    return result;
  }

  /**
   * Redact the Fault.Message when the fault code is in the sensitive
   * fault codes list (e.g. session expired fault code 109).
   */
  static redactFaultMessage(data: ApiFaultOrWarnings): ApiFaultOrWarnings {
    const sanitized = clone(data);

    if (sanitized.Fault) {
      sanitized.Fault.Message =
        (data.Fault.Code && REDACTED_FAULT_MESSAGES_MAP[data.Fault.Code]) ||
        sanitized.Fault.Message;
    }

    return sanitized;
  }

  /**
   * Redact request body and response body for restricted modules
   * (CustomerCare, Configuration, Reporting, Security).
   */
  static redactRestrictedModulePayload(
    error: CapturedApiError
  ): CapturedApiError {
    if (
      !ERROR_LOG_RESTRICTED_MODULES.includes(
        error.applicationContext.moduleName
      )
    ) {
      return error;
    }

    const result = clone(error);
    result.applicationContext.apiMessage.config.data = REDACTED_MESSAGE;
    result.isBodyOrPayloadRedacted = true;

    if (result.applicationContext.apiMessage.response) {
      result.applicationContext.apiMessage.response.data = REDACTED_MESSAGE;
    }

    return result;
  }

  private static encodeBaseUrl(error: CapturedError): CapturedError {
    const apiMessage = error.applicationContext?.apiMessage as
      | CapturedErrorApiMessage
      | Record<string, never>;
    if ('config' in apiMessage && apiMessage.config?.baseURL) {
      const result = clone(error);
      const resultApiMessage = result.applicationContext
        .apiMessage as CapturedErrorApiMessage;
      resultApiMessage.config.baseURL = encodeURIComponent(
        apiMessage.config.baseURL
      );
      return result;
    }
    return error;
  }

  private static removeResponseConfigDuplicate(
    error: CapturedError
  ): CapturedError {
    const apiMessage = error.applicationContext
      ?.apiMessage as CapturedErrorApiMessage;
    if (apiMessage?.response?.config) {
      const result = clone(error);
      const resultApiMessage = result.applicationContext
        .apiMessage as CapturedErrorApiMessage;
      delete resultApiMessage.response?.config;
      return result;
    }
    return error;
  }

  private static redactSingleHeader(
    apiMessage: CapturedErrorApiMessage,
    messageProperty: CapturedErrorApiMessageConfigPath,
    header: string
  ): CapturedErrorApiMessage {
    const transformed = clone(apiMessage);

    return update(transformed, `${messageProperty}.headers`, (headers) =>
      mapValues(headers, (value, key) => {
        if (
          key.localeCompare(header, undefined, {
            usage: 'search',
            sensitivity: 'base'
          }) === 0
        ) {
          return REDACTED_MESSAGE;
        }
        return value;
      })
    );
  }
}
